<template>
	<div id="invoice" style="margin: -8px -12px">
		<!-- confirm discard dialog -->
		<v-dialog v-model="confirmDiscardDialog" width="400" persistent>
			<v-card>
				<v-card-title class="alert-color--text py-0">
					<v-spacer/>
					<span class="ms-8 text-uppercase">
						{{$t('delete')}}
					</span>
					<v-spacer/>

					<v-btn @click="confirmDiscardDialog = false" icon dark>
						<v-icon color="alert-color">mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-4 pb-2">
					{{ $t('messages.are-you-sure-to-delete-invoice-draft') }}
				</v-card-text>
				<v-card-actions>
					<v-spacer/>

					<v-btn
						large
						height="34"
						@click="confirmDiscardDialog = false"
						:disabled="deleteLoading"
					>
						{{$t('cancel')}}
					</v-btn>
					<v-btn
						color="alert-color white--text"
						large
						height="34"
						@click="deleteInvoice()"
						:loading="deleteLoading"
					>
						{{$t('confirm')}}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!--dialog payment method  -->
		<v-dialog v-model="dialogPaymentMethod" width="400">
			<v-card>
				<v-card-title class="primary--text text-center py-1">
					<v-spacer/>
					<span class="ms-8 text-uppercase">
						{{$t('payment-method.label')}}
					</span>
					<v-spacer />
					<v-btn @click="dialogPaymentMethod = false" icon dark>
						<v-icon color="primary">mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pb-0 pt-1">
					{{ $t('PurchaseInvoice.the-net-invoice-must-be-zero-and-this-requires-zeroing-all-financial-fields') }}
				</v-card-text>

				<v-card-actions>
					<v-spacer />
					<v-btn @click="resetPrice()" text color="success">
						{{$t('ok')}}
					</v-btn>

					<v-btn @click="dialogPaymentMethod = false" text color="alert-color">
						{{$t('cancel')}}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- confirm payment method  -->
		<v-dialog v-model="confirmPaymentMethodDialog" :persistent="submitLoading" width="400">
			<v-card>
				<v-card-title class="primary--text text-center py-1">
					<v-spacer/>
					<span class="ms-8 text-uppercase">
						{{$t('payment-details.title')}}
					</span>
					<v-spacer />
					<v-btn icon dark :disabled="submitLoading" @click="confirmPaymentMethodDialog = false">
						<v-icon color="primary">mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pb-0 pt-1">
					<!-- payment method -->
					<div class="d-flex justify-space-between flex-wrap mb-2">
						<span class="text-subtitle-2 font-weight-medium">{{$t('payment-method.label')}}</span>
						<v-chip-group
							v-model="submitData.paymentMethodId"
							mandatory
							show-arrows
							@change="
								submitData.paymentMethodId === paymentMethod.internal && entries.length
									? dialogPaymentMethod = true
									: null"
						>
							<v-chip
								v-for="(item, i) in paymentMethodArray"
								:key="i"
								:value="item.id"
								:disabled="isReadonly || isCurrentInvoicePosted || loading || submitLoading"
								small
								:active-class="item.id === paymentMethod.creditCard ? 'edit-color white--text' : 'primary'"
								class="ms-1 my-0"
							>
								{{item.name}}
							</v-chip>
						</v-chip-group>
					</div>

					<template v-if="submitData.paymentMethodId === paymentMethod.debt">
						<!-- account -->
						<v-autocomplete
							v-model="submitData.account"
							:label="$t('forms.account')"
							:items="filteredAccounts"
							item-value="id"
							:item-text="(item) => `${item.accountingNo} - ${item.name}`"
							outlined
							dense
							hide-details
							class="mb-2"
							:readonly="isCurrentInvoicePosted"
							:rules="rules.required"
							:disabled="isReadonly || selfPermissions.invoiceChangeAccount.isNone"
							@change="submitData.paymentMethodId !==  paymentMethod.internal && entries.length !== 0 ? alertDialog = true : null"
						></v-autocomplete>
						
						<!-- paid -->
						<v-text-field
							v-model="paidFormatted"
							v-number-format.number="[paidFormatted, submitData.paid]"
							:label="$t('forms.paid')"
							dense
							hide-details
							class="mb-1"
							outlined
							:disabled="submitLoading || loading || isReadonly"
							:readonly="isCurrentInvoicePosted"
							:loading="loading"
						></v-text-field>
						
						<!-- unpaid -->
						<div class="d-flex align-center edit-color--text">
							<span class="text-subtitle-2 me-2 font-weight-medium">
								{{$t('forms.unpaid')}}
							</span>
							<span class="text-subtitle-1 font-weight-bold">
								{{unpaidAmount}}
							</span>
						</div>
					</template>

					<!-- items prices -->
					<div v-if="useInvoicesCurrencies && submitData.currencyId !== $localStorage.currencyId" class="mt-2">
						<v-simple-table dense class="elevation-2 rounded-medium">
							<thead>
								<tr>
									<th style="width: 60%; min-width: 70%">{{$t('headers.item')}}</th>
									<th style="width: 20%; min-width: 30%">{{$t('headers.quantity')}}</th>
									<th style="width: 20%; min-width: 30%">{{$t('headers.price')}}</th>
								</tr>
							</thead>
							<tbody>
								<!-- items -->
								<tr
									v-for="(entry, i) in entries"
									:key="i"
								>
									<td>{{getItemById(entry.itemId)?.name}}</td>
									<td>{{numberFormat(fixNumber(entry.quantity))}}</td>
									<td>
										{{
											numberFormat(
												fixNumber(entry.totalNet / (1 / (submitData.currencyPrice || 1)))
											)
										}}
									</td>
								</tr>

								<!-- total -->
								<tr>
									<td class="text-center text-body-2 font-weight-bold" colspan="3">
										<span class="primary--text me-2">
											{{$t('SaleInvoice.total-net')}}
										</span>
									
										{{
											numberFormat(
												fixNumber(totalNet / (1 / (submitData.currencyPrice || 1)))
											) + ' ' +
											getCurrencyById(submitData.currencyId)?.symbol ?? ''
										}}
									</td>
								</tr>
							</tbody>
						</v-simple-table>
					</div>
				</v-card-text>

				<v-card-actions>
					<v-spacer />
					<v-btn
						text
						color="primary"
						:loading="submitLoading"
						@click="
							checkout(
								confirmPaymentMethodUtils.needToPrint,
								confirmPaymentMethodUtils.needToPrint ? print.receipt : null
							)
						"
					>
						{{$t('print.title')}}
					</v-btn>

					<v-btn
						text
						:disabled="submitLoading"
						color="alert-color"
						@click="confirmPaymentMethodDialog = false"
					>
						{{$t('cancel')}}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		
		<!-- Dialog alert -->
		<v-dialog v-model="alertDialog" width="350px">
			<v-card>
				<v-card-title class="orange--text py-0">
					<v-spacer/>
					<span class="ms-8 text-uppercase">
						{{$t('SaleInvoice.alert')}}
					</span>
					<v-spacer/>

					<v-btn @click="alertDialog = false" icon dark>
						<v-icon color="orange">mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pb-0 pt-1">
					{{ $t('SaleInvoice.alert-message') }}
				</v-card-text>
				<v-card-actions>
					<v-spacer/>
					<v-btn
						color="orange white--text"
						large
						height="34"
						@click="fetchSuggestedPurchasePriceForAll()"
					>
						{{$t('ok')}}
					</v-btn>
					<v-btn
						large
						height="34"
						@click="alertDialog = false"
					>
						{{$t('cancel')}}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- date alert dialog -->
		<v-dialog v-model="dateAlert.dialog" max-width="350">
			<v-card>
				<v-card-title class="orange--text py-0">
					<v-spacer/>
					<span class="ms-8 text-uppercase">
						{{$t('alerts.alert')}}
					</span>
					<v-spacer/>

					<v-btn
						icon
						dark
						@click="dateAlert.isDateChecked = false; dateAlert.dialog = false;"
					>
						<v-icon color="orange">mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pb-0 pt-1">
					{{ $t('alerts.check-date.message') }}
				</v-card-text>
				<v-card-actions>
					<v-spacer/>
					<v-btn
						color="orange white--text"
						large
						height="34"
						@click="
							submitData.date = moment();
							checkout(dateAlert.needPrint, dateAlert.printType);
						"
					>
						{{$t('alerts.check-date.today-date')}}
					</v-btn>
					<v-btn
						large
						height="34"
						@click="checkout(dateAlert.needPrint, dateAlert.printType)"
					>
						{{$t('alerts.check-date.invoice-date')}}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-dialog v-model="processDialog" width="640px" content-class="rounded-lg">
			<v-card id="invoice" class="rounded">
				<v-card-title class="d-flex justify-space-between">
					<span class="text-h6 font-weight-bold">
						{{ $t('check-out')  }}
					</span>
					<div>
						<v-icon @click="processDialog = false" small>
							mdi-close
						</v-icon>
					</div>
				</v-card-title>
				<v-card-text class="pb-0">
					<v-row no-gutters class="py-2">
						<v-col class="d-flex flex-column justify-center">
							<span class="pb-2 black--text">
								{{$t("payment-method.label")}}
							</span>
							<v-btn-toggle
								v-model="submitData.paymentMethodId"
								class="grey-thin-border customized-rounded rounded pa-1 w-100 d-flex justify-space-between"
								dense
								hide-details
								mandatory
								borderless
								@change="
								submitData.paymentMethodId === paymentMethod.internal && entries.length
									? dialogPaymentMethod = true
									: null"
							>
								<v-btn
									v-for="(item, i) in paymentMethodArray"
									:key="i"
									depressed
									class="rounded px-3 transparent"
									:active-class="item.id === submitData.paymentMethodId ? 'primary white---text ' : ''"
									:disabled="isReadonly || isCurrentInvoicePosted || loading || submitLoading"
									:width="`${100 / (paymentMethodArray.length || 1)}%`"
									height="30px"
									small
									:value="item.id"
								>
									<span class="text-subtitle-2">
										{{ item.name }}
									</span>
								</v-btn>
							</v-btn-toggle>
						</v-col>
						<v-col cols="auto" class="px-2"></v-col>
						<v-col class="d-flex flex-column justify-center" cols="5">
							<span 
								class="pb-2 black--text"
							>
								{{$t("forms.account")}}
							</span>
							<v-autocomplete
								v-model="submitData.account"
								:items="filteredAccounts"
								append-icon="mdi-chevron-down mt-2"
								item-value="id"
								:item-text="(item) => `${item.accountingNo} - ${item.name}`"
								outlined
								dense
								hide-details
								class="customized-rounded maximized-height-input "
								:readonly="isCurrentInvoicePosted"
								:rules="rules.required"
								:disabled="isReadonly || selfPermissions.invoiceChangeAccount.isNone"
								@change="submitData.paymentMethodId !==  paymentMethod.internal && entries.length !== 0 ? alertDialog = true : null"
							></v-autocomplete>
							<!-- <v-card
								v-else
								flat
								height="40px"
								class="d-flex align-center"
							>
								<span class="black--text px-2">
									{{filteredAccounts.find(v => v.id === submitData.account)?.accountingNo + '-' + filteredAccounts.find(v => v.id === submitData.account)?.name }}
								</span>
							</v-card> -->
						</v-col>
						<v-col v-if="!hideTaxOnSaleInvoice" cols="12" class="d-flex justify-space-between py-5">
							<span class="black--text font-weight-bold">
								{{ $t('SaleInvoice.total-without-vat') }}
							</span>
							<span class="black--text font-weight-bold">
								{{numberFormat(entriesTotalWithoutVat + (!includeVatInPrice ? submitData.discount : 0))}}
							</span>
						</v-col>
						<v-col v-if="!hideTaxOnSaleInvoice" cols="12" class="d-flex justify-space-between py-2">
							<span class="black--text font-weight-bold">
								{{$t('forms.tax')}}
							</span>
							<span class="black--text font-weight-bold">
								{{numberFormat(vatAmount)}}
							</span>
						</v-col>
						<v-col class="py-5" cols="12">
							<span class="black--text font-weight-bold">
								{{$t('SaleInvoice.total-net')}}
							</span>
							<div class="d-flex align-center pt-2">
								<span class="text-h4 primary--text font-weight-bold pb-1">
									{{numberFormat(totalNet)}}
								</span>
							</div>
						</v-col>
					</v-row>
				</v-card-text>
				<v-card-actions class="d-flex justify-end pt-0 pb-4 px-2">
					<v-btn
						class="grey-thin-border "
						color="black"
						text
						width="80"
						height="46"
						@click="processDialog = false"
					>
						{{ $t('cancel')}}
					</v-btn>
					<v-btn
						color="primary  white--text "
						class="mx-3"
						large
						width="120"
						height="45"
						:disabled="submitLoading || loading || isCurrentInvoicePosted || totalNet < 0 || !submitData.account || !entries.length || isReadonly"
						:loading="submitLoading || loading"
						@click="confirmPaymentMethodUtils.needToPrint = true; checkout(
								confirmPaymentMethodUtils.needToPrint,
								confirmPaymentMethodUtils.needToPrint ? print.receipt : null
							)"
					>
						<v-icon class="mx-2">
							mdi-printer-outline
						</v-icon>
						{{ $t('check-out') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-row no-gutters>
			<v-col>
				<v-row no-gutters>
					<v-col cols="12">
						<v-card
							class="rounded-0 thin-border"
							width="100%"
							color=""
							flat
						>
							<v-row no-gutters class="py-2 px-3">
								<!-- back -->
								<v-col v-if="previousRoute" cols="auto" class="d-flex align-center pe-3">
									<v-btn
										height="40"
										depressed
										class="grey-thin-border rounded-lg"
										color="white"
										@click="$router.push(previousRoute)"
									>
										<v-icon v-if="!$vuetify.rtl" size="24" class="">
											mdi-chevron-left
										</v-icon>
										<v-icon v-if="$vuetify.rtl" size="24" class="">
											mdi-chevron-right
										</v-icon>
										<span class="mb-1 font-weight-bold">
											{{$t('back')}}
										</span>
									</v-btn>
								</v-col>
								<v-col cols="auto" class="d-flex align-center">
									<div class="d-flex align-center">
										<v-btn-toggle
											v-model="isSearchByBarcode"
											class="d-flex justify-space-between align-center  px-2 rounded-lg grey-thin-border"
											:class="$vuetify.rtl ? 'rounded-l-0' : 'rounded-r-0'"
											style="margin:0 -1px; height:40px; width: 74px"
											dense
											active-class="primary white--text"
											hide-details
											mandatory
										>

											<v-btn
												small
												width="24px"
												height="24px"
												icon
												class="rounded mx-0 grey-thin-border"
												:value="true"
											>
												<v-icon
													size="15"
													:color="isSearchByBarcode ? 'white': ''"
												>
													mdi-barcode-scan
												</v-icon>
											</v-btn>
											<v-btn
												icon
												small
												width="24px"
												height="24px"
												class="rounded mx-0 grey-thin-border "
												:value="false"
											>

												<v-icon
													size="15"
													:color="isSearchByBarcode ? '': 'white'"
												>
													mdi-format-letter-case
												</v-icon>
											</v-btn>
										</v-btn-toggle>
										<v-text-field
											v-model="searchItem"
											outlined
											dense
											:placeholder="$t('SaleInvoice.search-products')"
											class="rounded-lg maximized-height-input"
											:class="$vuetify.rtl ? 'rounded-r-0' : 'rounded-l-0'"
											hide-details
											@keypress.enter="isSearchByBarcode && !isCurrentInvoicePosted ? addByBarcode() : null"
										>
											<template
												v-slot:prepend-inner
											>
												<v-icon size="22" class="">
													mdi-magnify
												</v-icon>
											</template>
										</v-text-field>
									</div>
								</v-col>
								<v-col cols="auto" class="px-3">
									<v-divider class="grey-thin-border" vertical />
								</v-col>
								<v-col v-if="configs.canDraft" class="d-flex align-center">
									<invoices-tabs
										:event-hub="eventHub"
										:disable="isReadonly"
									/>
								</v-col>
							</v-row>
						</v-card>
					</v-col>
				</v-row>
				<v-row no-gutters>
					<v-col cols="auto" >
						<v-card
							class=" rounded-0 thin-border pb-3 pt-4 justify-space-between"
							width="225px"
							flat
							color="primary-lighten"
						>
							<div class="px-3">
								<span class="text-body-1 font-weight-bold">
									{{$t('SaleInvoice.main-items')}}
								</span>
								<v-btn-toggle
									v-model="activeItems"
									class="grey-thin-border customized-rounded pa-1 mt-4 mb-2 w-100 d-flex justify-space-between"
									dense
									hide-details
									mandatory
									borderless
								>
									<v-btn
										depressed
										class="rounded px-3 transparent"
										width="49%"
										active-class="primary white---text "
										small
										:value="0"
									>
										<span class="text-subtitle-2">
											{{$t('SaleInvoice.all')}}
										</span>
									</v-btn>
									<v-btn
										depressed
										class="rounded px-3 transparent"
										width="49%"
										active-class="primary white---text "
										small
										color="transparent"
										:value="1"

									>
										<span class="text-subtitle-2">
											{{$t('SaleInvoice.favorite')}}
										</span>
									</v-btn>
								</v-btn-toggle>
							</div>
							<v-card
								:height="$vuetify.breakpoint.height - 230"
								class="overflow-auto px-3"
								flat
							>
								<v-hover
									v-for="item in mainItems"
									:key="item.id"
									v-slot="{ hover }"
								>
									<v-card 
										outlined 
										min-height="52px"
										class="px-2 my-2 text-small customized-rounded d-flex align-center"
										:class="{'active-item-bg': selectedItem.id === item.id || item.id === 0, 'hovered-item-bg': hover}"
										@click="selectedItem = item"
									>
										<span class="text-truncate text-subtitle-2" style="width: 160px">
											{{ item.name }}
										</span>
										<v-card class="primary white--text rounded text-center text-subtitle-2" min-width="22px" max-width="22px" flat>
											{{ item?.children?.length }}
										</v-card>
									</v-card>
								</v-hover>
							</v-card>
						</v-card>
					</v-col>
					<v-col style="width: calc(100% - 225px)">
						<v-card
							class="rounded-0 overflow-auto px-2 py-1"
							width="100%"
							:height="$vuetify.breakpoint.height - 265"
							color=""
							outlined
						>
							<v-row v-for="(group) in subItems" :key="group.id" no-gutters >
								<v-col v-if="group.id && group.items.length" cols="12" class="px-3 pt-2">
									<span class="text-h6 font-weight-bold primary--text">
										{{ items.find(v => v.id === group.id)?.name }}
									</span>
								</v-col>
								<v-col
									v-for="item in group.items"
									:key="item.id"
									cols="auto"
								>
									<v-hover v-slot="{ hover }">
										<v-card
											class="customized-rounded px-2 pb-2 pt-1 d-flex flex-column ma-2"
											:class="{'active-item-bg': itemIsSelected(item.id), 'hovered-item-bg': hover}"
											:disabled="submitLoading || loading || isCurrentInvoicePosted ||  isReadonly || loadingRequestPrice"
											height="fit-content"
											outlined
											@click="addFavItem(item)"
										>
											<!-- <v-checkbox :input-value="itemIsSelected(item.id)" dense hide-details class="ma-0 d-flex" readonly  :ripple="false" >
												<template v-slot:label>
													
												</template>
											</v-checkbox> -->
											<span class="black--text my-2" :class="{'primary--text': itemIsSelected(item.id)}">
												{{ item.name}}
											</span>
											<div class="d-flex align-center justify-space-between pt-1">
												<v-chip
													class=" px-2 thin-border customized-rounded transparent"
													height="20px"
												>
													{{getCurrencyById(item?.salePrice?.currencyId)?.symbol}}
													{{item?.salePrice?.amount || 0 }} 
												</v-chip>
												<span class="mx-1"></span>
												<v-chip
													class="d-flex align-center white--text customized-rounded thin-border transparent px-2"
													height="20px"
													:class="{'primary': itemIsSelected(item.id)}"
												>
													<v-icon class="black--text" size="18" :class="{'white--text': itemIsSelected(item.id)}">
														mdi-plus
													</v-icon>
													<span>
														{{ entries.find(v => v.itemId === item.id)?.quantity}}
													</span>
												</v-chip>
											</div>
										</v-card>
									</v-hover>
								</v-col>
							</v-row>
						</v-card>
						<v-card class="w-100 thin-border  rounded-0 over"  outlined height="150px">
							<v-row class="ma-0" no-gutters>
								<v-col cols="12" class="py-2">
									<span class="text-h6 font-weight-bold primary--text px-4">
										{{ $t('SaleInvoice.extra') }}
									</span>
									<!-- <v-slide-group
										multiple
										show-arrows
										>
										<v-slide-item
											v-for="n in 25"
											:key="n"
											v-slot="{ active, toggle }"
										>
											<v-btn
											class="mx-2"
											:input-value="active"
											active-class="purple white--text"
											depressed
											rounded
											@click="toggle"
											>
											Options {{ n }}
											</v-btn>
										</v-slide-item>
										</v-slide-group> -->
										<v-card
											class=""
											flat
											max-width="100%"
											min-width="100%"
										>
											<v-slide-group
												show-arrows
												:style="{
													maxWidth: $vuetify.breakpoint.lgAndUp ? '50vw' : '100%'
												}"
											>
												<v-slide-item
													v-for="(item, n) in extraItems"
													:key="n"
												>
													<v-hover v-slot="{ hover }">
														<v-card
															class="customized-rounded px-2 pb-2 pt-1 d-flex flex-column ma-2"
															:class="{'active-item-bg': itemIsSelected(item.id), 'hovered-item-bg': hover}"
															:disabled="submitLoading || loading || isCurrentInvoicePosted ||  isReadonly || loadingRequestPrice"
															height="fit-content"
															outlined
															@click="addFavItem(item)"
														>
															<!-- <v-checkbox :input-value="itemIsSelected(item.id)" dense hide-details class="ma-0 d-flex" readonly  :ripple="false" >
																<template v-slot:label>
																	
																</template>
															</v-checkbox> -->
															<span class="black--text my-2" :class="{'primary--text': itemIsSelected(item.id)}">
																{{ item.name}}
															</span>
															<div class="d-flex align-center justify-space-between pt-1">
																<v-chip
																	class=" px-2 thin-border customized-rounded transparent"
																	height="20px"
																>
																	{{getCurrencyById(item?.salePrice?.currencyId)?.symbol}}
																	{{item?.salePrice?.amount || 0 }} 
																</v-chip>
																<span class="mx-1"></span>
																<v-chip
																	class="d-flex align-center white--text customized-rounded thin-border transparent px-2"
																	height="20px"
																	:class="{'primary': itemIsSelected(item.id)}"
																>
																	<v-icon class="black--text" size="18" :class="{'white--text': itemIsSelected(item.id)}">
																		mdi-plus
																	</v-icon>
																	<span>
																		{{ entries.find(v => v.itemId === item.id)?.quantity}}
																	</span>
																</v-chip>
															</div>
														</v-card>
													</v-hover>
												</v-slide-item>
											</v-slide-group>
									</v-card>
									
								</v-col>
							</v-row>
						</v-card>
					</v-col>
				</v-row>
			</v-col>
			<v-col cols="12" lg="auto">
				<v-card
					class="rounded-0 thin-border py-2"
					flat
					color="primary-lighten"
					:height="$vuetify.breakpoint.height - 54"
					:width="$vuetify.breakpoint.lgAndUp ? '550px': '100%'"
				>
					<v-row no-gutters class="py-1 px-4">
						<v-col cols="auto">
							<span class="text-h6 font-weight-bold primary--text">
								{{ $t('SaleInvoice.invoice-details')}}
							</span>
						</v-col>
						<v-col>

						</v-col>
						<!-- createdBy -->
						<v-col
							v-if="
								(isAdmin || permissionsForUser.userType === userType.manager) &&
								auditLogCreatedBy
							"
							cols="auto"
							class="d-flex align-center px-3"
						>
							<v-menu offset-y open-on-hover>
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										v-bind="attrs"
										v-on="on"
										icon
										elevation="0" 
										color=" primary" 
										:disabled="submitLoading || loading"
									>
										<v-icon size="24">mdi-information-variant-circle-outline</v-icon>
									</v-btn>
								</template>
								<v-list dense class="py-0">
									<v-list-item class="text-body-2">
										<span>{{$t('audit-log.created-by')}}:</span>
										<span class="font-weight-bold ms-1">{{auditLogCreatedBy.username}}</span>
										<template v-if="auditLogCreatedBy.time">
											<span class="mx-1">-</span>
											{{$t('audit-log.time')}}:
											<span class="font-weight-bold ms-1">
												{{
													$i18n.locale === 'ar'
														? auditLogCreatedBy.time
															.replace(/am|pm/g, m => ({ am: 'ص', pm: 'م' })[m])
														: auditLogCreatedBy.time
												}}
											</span>
										</template>
									</v-list-item>
								</v-list>
							</v-menu>
						</v-col>
						<!-- preview -->
						<v-col
							v-if="
								!selfPermissions.allInvoices.isNone ||
								!selfPermissions.itemJournal.isNone ||
								!selfPermissions.cashJournal.isNone
							"
							cols="auto"
						>
							<v-menu offset-y>
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										dark
										v-bind="attrs"
										v-on="on"
										elevation="0" 
										color="primary white--text" 
										class="rounded-lg"
										height="35" 
										:disabled="submitLoading || loading "
									>
										{{$t('show')}}
										<v-icon size="20" class="ms-1">mdi-chevron-down</v-icon>
									</v-btn>
								</template>
								<v-list dense>
									<v-list-item 
										v-if="!selfPermissions.allInvoices.isNone"
										:to="{
											name: 'all-invoices',
											query: { type: invoiceTypes.sales }
										}"
									>
										<v-list-item-title> 
											{{$t('all-invoice')}}
										</v-list-item-title>
									</v-list-item>

									<v-list-item 
										v-if="!selfPermissions.itemJournal.isNone"
										:to="{
											name: 'journal-item',
											params: {
												id: getInvoiceById($route.params.id)?.itemsJournalId
											}
										}"
										:disabled="!getInvoiceById($route.params.id)?.isPosted"
									>
										<v-list-item-title> 
											{{$t('view-invoice-details.related-items-journal')}}
										</v-list-item-title>
									</v-list-item>

									<v-list-item 
										v-if="!selfPermissions.cashJournal.isNone"
										:to="{
											name: 'accounting-journal',
											params: {
												id: getInvoiceById($route.params.id)?.cashJournalId
											}
										}"
										:disabled="!getInvoiceById($route.params.id)?.isPosted"
									>
										<v-list-item-title> 
											{{$t('view-invoice-details.related-cashbox-journal')}}
										</v-list-item-title>
									</v-list-item>
								</v-list>
							</v-menu>
						</v-col>
					</v-row>
					<v-row no-gutters class="px-4 py-3">
						<v-col cols="auto" class="d-flex align-center">
							<span>
								{{ $t('invoice-no') }}
							</span>
							<v-card flat width="180px" class="d-flex px-3">
								<items-navigator
									v-model="invoiceNo"
									:disabled="loading"
									class="customized-rounded"
									:items="
										invoices.filter(
											c => permissionsForUser.userType === userType.casher
											? c.cashboxId === permissionsForUser.cashboxId
											: true
										)
									"
								></items-navigator>
							</v-card>
						</v-col>
						<v-col class="px-2" cols="auto">
							<v-divider class="thin-border" vertical />
						</v-col>
						<v-col>
							<!-- <v-text-field
								v-model="submitData.date"
								:label="$t('forms.date')"
								dense
								type="date"
								hide-details
								class="mb-1"
								outlined
								:disabled="submitLoading || loading || isReadonly"
								:readonly="isCurrentInvoicePosted"
							></v-text-field> -->
							<!-- <template>
								<v-menu
									v-model="menu"
									:close-on-content-click="false"
									transition="scale-transition"
									offset-y
								>
									<template v-slot:activator="{ on, attrs }">
									<v-text-field
										v-model="formattedDate"
										label="Select Date"
										readonly
										outlined
										dense
										class="customized-rounded white"
										append-icon="mdi-date"
										v-bind="attrs"
										v-on="on"
									></v-text-field>
									</template>

									<v-date-picker
									v-model="date"
									@input="formatDate"
									></v-date-picker>
								</v-menu>
							</template> -->
								<!-- :loading="loading" -->
								<!-- date -->
							<bee-date-input
								v-model="submitData.date"
								:label="$t('forms.date')"
								input-format-order="DD-MM-YYYY"
								prepend-icon="mdi-calendar"
								dense
								hide-details
								class="mb-1"
								:disabled="submitLoading || loading || isReadonly"
								:readonly="isCurrentInvoicePosted"
								:loading="loading"
								fit-inputs
								iso-date
							></bee-date-input>
						</v-col>
					</v-row>
					<v-row v-if="useInvoicesCurrencies" no-gutters class="px-4 pb-2">
						<v-col cols="4" class="d-flex align-center">
							<span class="me-2">
								{{$t('forms.currency')}}
							</span>
							<v-autocomplete
								v-model="submitData.currencyId"
								:items="currencies"
								item-text="symbol"
								item-value="id"
								class="customized-rounded"
								:disabled="submitLoading || loading || isCurrentInvoicePosted"
								:loading="loading"
								append-icon
								persistent-placeholder
								dense
								hide-details
								outlined
								@change="() => {
									if (submitData.currencyId === $localStorage.currencyId) {
										formattedCurrencyPrice = '0';
										submitData.currencyPrice = 0;
									} else {
										formattedCurrencyPrice = '1';
										submitData.currencyPrice = 1;
									}
								}"
							></v-autocomplete>
						</v-col>
						<!-- <v-col class="px-4" cols="auto">
							<v-divider vertical />
						</v-col> -->
						<!-- currencyPrice -->
						<v-col
							cols="4"
							class="d-flex align-center px-4"
						>
							<span class="me-2 text-no-wrap">
								{{$t('forms.currency-price')}}
							</span>
							<v-text-field
								:value="submitData.currencyId === $localStorage.currencyId ? 1 : formattedCurrencyPrice"
								v-number-format.number="[formattedCurrencyPrice, submitData.currencyPrice]"
								dense
								:disabled="submitLoading || loading || submitData.currencyId === $localStorage.currencyId || isCurrentInvoicePosted"
								:loading="loading"
								class="customized-rounded"
								hide-details
								outlined
								persistent-placeholder
								@focus="e => e.target.select()"
								@change="formattedCurrencyPrice = $event"
							></v-text-field>
						</v-col>
					</v-row>
					<v-row no-gutters class="px-2">
						<v-col>
							<v-card flat :height="$vuetify.breakpoint.height - (useInvoicesCurrencies ? 442  : 399)" class="overflow-auto transparent">
								<v-expansion-panels
									v-if="entries.length"
									flat
								>
									<v-expansion-panel
										v-for="(item, index) in entries"
										:key="item.id"
										id="invoice-entry-expansion-panel"
										outlined
										class="thin-border customized-rounded pa-0 my-1"
									>
										<v-expansion-panel-header class="d-flex px-3 py-2" hide-actions>
											<template v-slot:default="{ open }">
												<v-row no-gutters class="ma-0">
													<v-col
														cols="auto"
														class="d-flex align-center"
													>
														<v-icon>
															{{ open 
																? 'mdi-chevron-down' 
																: $i18n.locale === 'ar' 
																	? 'mdi-chevron-left'
																	: 'mdi-chevron-right'
															}}
														</v-icon>
													</v-col>
													<v-col 
														cols="auto" 
														class="d-flex align-center px-1 pt-1"
													>
														<v-card
															flat
															width="fit-content"
															class="transparent"
															max-width="110px"
															
														>
															<v-text-field
																v-model="item.quantity"
																type="number"
																append-outer-icon="mdi-plus grey rounded mb-2 lighten-3"
																prepend-icon="mdi-minus grey rounded mb-2 lighten-3	"
																dense
																:disabled="submitLoading || loading || isCurrentInvoicePosted"
																outlined
																class="rounded white centered-input-text minimized-input-padding"
																hide-details
																hide-spin-buttons
																@click:prepend="
																	item.quantity > 1
																	? (
																		item.quantity--,
																		item.amount = item.quantity * item.price,
																		item.discount = item.amount * (item.percentDiscount / 100),
																		item.totalNet =
																			item.amount - item.discount + (
																				!includeVatInPrice
																					? ((item.taxPercent || 0) / 100) * (item.amount - item.discount)
																					: 0
																			)
																	)
																	: null;
																"
																@click:append-outer="
																	item.quantity++
																	item.amount = item.quantity * item.price;
																	item.discount = item.amount * (item.percentDiscount / 100)
																	item.totalNet =
																		item.amount - item.discount + (
																			!includeVatInPrice
																				? ((item.taxPercent || 0) / 100) * (item.amount - item.discount)
																				: 0
																		);
																"
																@change="
																	if (item.quantity <= 0) {
																		item.quantity = 1
																	}
																	item.amount = item.quantity * item.price;
																	item.discount = item.amount * (item.percentDiscount / 100);
																	item.totalNet =
																		item.amount - item.discount + (
																			!includeVatInPrice
																				? ((item.taxPercent || 0) / 100) * (item.amount - item.discount)
																				: 0
																		)
																"
																@click.stop	
																@mousedown.stop
															/>
														</v-card>
													</v-col>
													<v-col class="d-flex align-center px-2">
														<span class="text-subtitle-2 font-weight-bold">
															{{ items.find(v => v.id === item.itemId)?.name }}
														</span>
													</v-col>
													<v-col cols="auto" class="d-flex align-center px-1">	
														<v-card
															flat
															height="32px"
															class="grey lighten-3 px-2 rounded d-flex align-center main--text"
															dir="ltr"
														>
															<span>
																{{ item.quantity }}
															</span>
															<span class="mx-1">
																x
															</span>
															<span>
																{{ item.price }}																
															</span>
														</v-card>
													</v-col>
													<v-col v-if="item.discount" cols="auto" class="d-flex align-center px-1">
														<v-card
															flat
															height="32px"
															class="red lighten-5 px-2 h-100 rounded text-decoration-line-through text-caption d-flex align-center red--text"
															dir="ltr"
														>
															{{ fixNumber(item.amount, 2) }}
														</v-card>
													</v-col>
													<v-col cols="2" class="d-flex align-center px-2">
														<v-text-field
															:value="fixNumber(item.totalNet, 2)"
															outlined
															dense
															:disabled="true"
															class="rounded centered-input-text minimized-input-padding"
															hide-details
														>

														</v-text-field>
													</v-col>
													<v-col cols="auto" class="d-flex align-center">
														<v-btn 
															icon 
															class="red white--text rounded " 
															width="32" 
															height="32" 
															:disabled="submitLoading || loading || isReadonly || isCurrentInvoicePosted"
															@click.stop	
															@mousedown.stop
															@click="deleteItem({ index })"
														>
															<v-icon size="20">
																mdi-delete-outline
															</v-icon>
														</v-btn>
													</v-col>
												</v-row>
											</template>
										</v-expansion-panel-header>
										<v-expansion-panel-content>
											<v-row no-gutters class="px-2">
												<v-col cols="12" class="pb-2">
													<v-divider>
													</v-divider>
												</v-col>
												<v-col class="px-1 pb-2">
													<span class="text-subtitle-2">
														{{ $t("headers.quantity")}}
													</span>
													<div class="grey-thin-border customized-rounded px-2 mt-1">
														<v-text-field
															v-model.number="item.quantity"
															append-outer-icon="mdi-plus grey rounded mb-1 lighten-4"
															prepend-icon="mdi-minus grey rounded mb-1 lighten-4"
															type="number"
															hide-spin-buttons
															dense
															outlined
															class=" white centered-input-text rounded  minimized-height-input d-flex align-center minimized-input-padding"
															hide-details
															:disabled="submitLoading || loading || isCurrentInvoicePosted"
															@click:prepend="
																	item.quantity > 1
																	? (
																		item.quantity--,
																		item.amount = item.quantity * item.price,
																		item.discount = item.amount * (item.percentDiscount / 100),
																		item.totalNet =
																		item.amount - item.discount + (
																			!includeVatInPrice
																				? ((item.taxPercent || 0) / 100) * (item.amount - item.discount)
																				: 0
																		)
																	)
																	: null;
																"
															@click:append-outer="
																item.quantity++
																item.amount = item.quantity * item.price;
																item.discount = item.amount * (item.percentDiscount / 100)
																item.totalNet =
																	item.amount - item.discount + (
																		!includeVatInPrice
																			? ((item.taxPercent || 0) / 100) * (item.amount - item.discount)
																			: 0
																	);
															"
															@change="
																	if (item.quantity <= 0) {
																		item.quantity = 1
																	}
																	item.amount = item.quantity * item.price;
																	item.discount = item.amount * (item.percentDiscount / 100);
																	item.totalNet =
																		item.amount - item.discount + (
																			!includeVatInPrice
																				? ((item.taxPercent || 0) / 100) * (item.amount - item.discount)
																				: 0
																		)
																"
														/>
													</div>
												</v-col>
												<v-col cols="4"  class="px-1">
													<span class="text-subtitle-2 ">
														{{ $t('headers.price')}}
													</span>
													<v-text-field
														:value="item.price"
														type="number"
														hide-spin-buttons
														outlined
														dense
														disabled
														class="rounded-lg maximized-height-input minimized-input-padding white centered-input-text mt-1"
														hide-details
													/>
												</v-col>
												<v-col cols="4"  class="px-1">
													<span class="text-subtitle-2">
														{{ $t('headers.amount')}}
													</span>
													<v-text-field
														v-model.number="item.amount"
														type="number"
														hide-spin-buttons
														outlined
														:disabled="submitLoading || loading || isCurrentInvoicePosted"
														class="customized-rounded maximized-height-input white centered-input-text mt-1"
														dense
														hide-details
														@change="
															if(fixNumber(item.amount / item.price, 2) <= 0) {
																item.amount = item.price
															}
															item.quantity = fixNumber(item.amount / item.price, 2);
															// to update the values after changing the quantity
															item.discount = item.amount * (item.percentDiscount / 100),
															item.totalNet =
															item.amount - item.discount + (
																!includeVatInPrice
																	? ((item.taxPercent || 0) / 100) * (item.amount - item.discount)
																	: 0
															)
														"
													/>
												</v-col>
											</v-row>
											<v-row no-gutters class="px-2 pb-3">
												<v-col
													v-if="!selfPermissions.invoiceEntriesDiscount.isNone"
													cols="4"
													class="px-1"
												>
													<span class="text-subtitle-2 ">
														{{ $t('headers.discount')}}
													</span>
													<div class="d-flex align-center mt-1">
														<v-btn-toggle
															v-model="discountType"
															class="d-flex justify-space-between align-center  px-1 rounded-lg grey-thin-border"
															:class="$vuetify.rtl ? 'rounded-l-0' : 'rounded-r-0'"
															style="margin:0 -1px 0 0; height:40px; width: 80px"
															dense
															:disabled="submitLoading || loading || isCurrentInvoicePosted"
															active-class="primary white--text"
															hide-details
															mandatory
														>

															<v-btn
																small
																width="24px"
																height="24px"
																icon
																:disabled="submitLoading || loading"
																class="rounded mx-1 grey-thin-border"
																:value="false"
															>
																<v-icon
																	size="15"
																	:color="discountType ? '': 'white'"
																>
																	mdi-brightness-percent
																</v-icon>
															</v-btn>
															<v-btn
																icon
																small
																width="24px"
																height="24px"
																:disabled="submitLoading || loading"
																class="rounded mx-1 grey-thin-border "
																:value="true"
															>

																<v-icon
																	size="15"
																	:color="discountType ? 'white': ''"
																>
																	mdi-cash
																</v-icon>
															</v-btn>
														</v-btn-toggle>
														<v-text-field
															:value="discountType ? fixNumber(item.discount, 1) : fixNumber(item.percentDiscount, 1)"
															type="number"
															hide-spin-buttons
															outlined
															dense
															:disabled="submitLoading || loading || isCurrentInvoicePosted"
															class="rounded-lg maximized-height-input minimized-input-padding white centered-input-text "
															:class="$vuetify.rtl ? 'rounded-r-0' : 'rounded-l-0'"
															hide-details
															@change="
																discountType 
																	? (item.discount = Number($event), item.percentDiscount = fixNumber(item.discount / (item.amount) * 100, 2))
																	: (item.percentDiscount = Number($event), item.discount = fixNumber(((item.amount) * item.percentDiscount)/ 100, 2));
																item.totalNet = fixNumber(item.amount - item.discount + ((item.taxPercent || 0) / 100) *(item.amount - item.discount),2);
															"	
														/>
													</div>
												</v-col>
												<v-col cols="4" class="px-1" v-if="!includeVatInPrice">
													<span class="text-subtitle-2">
														{{ $t('headers.tax')}}
													</span>
													<v-text-field
														v-model="item.taxPercent"
														type="number"
														hide-spin-buttons
														outlined
														:disabled="submitLoading || loading || isCurrentInvoicePosted"
														class="customized-rounded maximized-height-input white centered-input-text mt-1"
														dense
														hide-details
														@change="
															item.totalNet = fixNumber(item.amount - item.discount + ((item.taxPercent || 0) / 100) *(item.amount - item.discount),2)
														"
													/>
												</v-col>
												<v-col cols="4"	 class="px-1">
													<span class="text-subtitle-2">
														{{ $t('headers.barcode')}}
													</span>
													<v-text-field
														v-model="item.barcode"
														outlined
														:disabled="true"
														class="customized-rounded maximized-height-input white centered-input-text mt-1"
														dense
														hide-details
													/>
												</v-col>
											</v-row>
										</v-expansion-panel-content>
									</v-expansion-panel>
								</v-expansion-panels>
								<v-card v-else flat height="100%" width="100%" class="d-flex flex-column justify-center align-center transparent">
									<span class="text-body-1 font-weight-bold mb-2	">
										{{$t('SaleInvoice.did-not-add-any-items-yet')}}
									</span>
									<span class="text-subtitle-2">
										{{$t('SaleInvoice.choose-item-to-add')}}
										
									</span>
								</v-card>
							</v-card>
						</v-col>
					</v-row>
					<v-row no-gutters class="px-4 thin-border ">
						<v-col v-if="!selfPermissions.invoiceDiscount.isNone" cols="4" class="d-flex flex-column pt-2 pb-4">
							<span>
								{{$t('headers.discount')}}
							</span>
							<div class="d-flex align-center  mt-2">
								<v-btn-toggle
									v-model="invoiceDiscountType"
									class="d-flex justify-space-between align-center px-1 rounded-lg grey-thin-border"
									:class="$vuetify.rtl ? 'rounded-l-0' : 'rounded-r-0'"
									style="margin:0 -1px 2px 0; height:40px; width: 75px"
									active-class="primary white--text"
									dense
									hide-details
									mandatory
								>

									<v-btn
										small
										width="24px"
										height="24px"
										icon
										class="rounded  grey-thin-border mx-1"
										:disabled="submitLoading || loading || !totalNet"
										:value="false"
									>
										<v-icon
											size="15"
											:color="invoiceDiscountType ? '': 'white'"
										>
											mdi-brightness-percent
										</v-icon>
									</v-btn>
									<v-btn
										icon
										small
										width="24px"
										height="24px"
										:disabled="submitLoading || loading || !totalNet"
										class="rounded grey-thin-border mx-1"
										:value="true"
									>

										<v-icon
											size="15"
											:color="invoiceDiscountType ? 'white': ''"
										>
											mdi-cash
										</v-icon>
									</v-btn>
								</v-btn-toggle>
								<v-text-field
									:value="invoiceDiscountType ? discountAmount : discountPercentage"
									type="number"
									hide-spin-buttons
									dense
									hide-details
									outlined
									:disabled="submitLoading || loading || isReadonly || !totalNet || isCurrentInvoicePosted"
									:readonly="isCurrentInvoicePosted"
									:loading="loading"
									class="rounded-lg maximized-height-input white"
									:class="$vuetify.rtl ? 'rounded-r-0' : 'rounded-l-0'"
									@change="
										submitData.discount = 0;
										// here you shouldn't add the discount to total Net becuase the total Net is decreased by the submitData.discount and it will be counted after calculate the discount amount and percentage
										invoiceDiscountType 
											? (discountAmount = Number($event), discountPercentage = fixNumber(discountAmount / (totalNet) * 100, 2))
											: (discountPercentage = Number($event), discountAmount = fixNumber(((totalNet) * discountPercentage)/ 100, 2));
										// here will recalculate the submitData.discount
										submitData.discount = Number(discountAmount)
									"
								/>
							</div>
						</v-col>
						<v-col cols="auto" class="px-2" />
						<v-col class="d-flex flex-column pt-2">
							<div class="d-flex flex-column">
								<span>
									{{$t('SaleInvoice.total-net')}}
								</span>
								<div class="d-flex align-center pt-2">
									<span class="text-h4 primary--text font-weight-bold pb-1">
										{{numberFormat(fixNumber(totalNet, 2))}}
										$
									</span>
									<v-card
										v-if="discountAmount"
										flat
										height="32px"
										width="fit-content"
										class="red lighten-5 px-2 h-100 rounded text-decoration-line-through text-subtitle-1 d-flex align-center red--text mx-3"
										dir="ltr"
									>
										{{
											fixNumber(Number(totalNet) + Number(discountAmount), 2)
										}}
									</v-card>
								</div>
							</div>
						</v-col>
					</v-row>
					<v-row no-gutters class="d-flex px-3">
						<v-col cols="12">
							<v-row no-gutters>
								<v-col class="d-flex flex-column py-2">
									<span class="">
										{{
											$t('forms.notes')
										}}
									</span>
									<v-text-field
										v-model="submitData.notes"
										outlined
										dense
										hide-details
										rows="1"
										no-resize
										auto-grow
										:disabled="submitLoading || loading || isReadonly || isCurrentInvoicePosted"
										:loading="loading"
										class="customized-rounded maximized-height-input  white mt-1"
									>

									</v-text-field>
								</v-col>
								<v-col cols="auto" class="px-2">

								</v-col>
								<v-col class="d-flex flex-column py-2">
									<span>
										{{$t('forms.printingNotes')}}
									</span>
									<v-text-field
										v-model="submitData.printingNotes"
										outlined
										dense
										hide-details
										class="customized-rounded maximized-height-input  white mt-1"
										:disabled="submitLoading || loading || isReadonly || isCurrentInvoicePosted"
										:loading="loading"
									/>
								</v-col>
							</v-row>
							<v-row no-gutters>
								<v-col>
									<v-btn
										v-if="!isCurrentInvoicePosted"
										elevation="0" 
										key="checkout"
										color="red white--text"
										class="rounded-lg w-100"
										height="40" 
										:disabled="submitLoading || loading || isReadonly"
										@click="confirmDiscardDialog = true"
									>
										<v-icon>
											mdi-delete-outline
										</v-icon>
										<span class="font-weight-bold">
											{{$t('delete')}}
										</span>
										
									</v-btn>
									<v-btn
										v-else-if="isAllowedToUnPost"
										elevation="0" 
										color="red white--text"
										class="rounded-lg w-100"
										key="un-post"
										height="40" 
										:disabled="submitLoading || loading"
										@click="unPostInvoice"
									>
										{{$t('un-post')}}
									</v-btn>
								</v-col>
								<v-col cols="auto" class="px-1">

								</v-col>
								<v-col cols="9">
									<v-btn
										elevation="0" 
										color="primary white--text" 
										class="rounded-lg w-100 px-3"
										:class="$vuetify.rtl ? 'rounded-l-0' : 'rounded-r-0'"
										height="40" 
										width="85%"
										:disabled="submitLoading || loading || isCurrentInvoicePosted || totalNet < 0 || !submitData.account || !entries.length || isReadonly"
										:loading="submitLoading || loading"
										@click="processDialog = true"
									>
										<v-row no-gutters>
											<v-col class="d-flex justify-start">
												{{ !isCurrentInvoicePosted ? $t('check-out') : $t('checked-out')}}
											</v-col>
										</v-row>
									</v-btn>
									<v-menu offset-y>
										<template v-slot:activator="{ attr, on }">
											<v-btn
												small
												height="40"
												width="15%"
												color="primary white--text"
												class="print-menu-btn text-capitalize pa-0"
												:class="$vuetify.rtl ? 'rounded-r-0' : 'rounded-l-0'"
												:disabled="
													submitLoading || loading || totalNet < 0 || !submitData.account || isReadonly
												"
												depressed
												v-on="on"
												v-bind="attr"
											>
												<v-icon size="20">mdi-chevron-down</v-icon>
											</v-btn>
										</template>

										<v-list dense>
											<v-list-item
												:disabled="isCurrentInvoicePosted || !entries.length"
												@click="confirmPaymentMethodDialog = true; confirmPaymentMethodUtils.needToPrint = false;"
											>
												<!-- @click="checkout(false)" -->
												<v-list-item-title>
													{{$t('print.pay-without-print')}}
												</v-list-item-title>
											</v-list-item>
											<v-list-item
												:disabled="!isCurrentInvoicePosted"
												@click="checkout(true, print.saleInvoiceReport)"
											>
												<v-list-item-title>
													{{$t('print.print-invoice')}}
												</v-list-item-title>
											</v-list-item>
											<v-list-item
												:disabled="!isCurrentInvoicePosted"
												@click="checkout(true, print.deliveryReport)"
											>
												<v-list-item-title>
													{{$t('print.print-delivery')}}
												</v-list-item-title>
											</v-list-item>
											<v-list-item
												:disabled="!isCurrentInvoicePosted"
												@click="checkout(true, print.receiveReport)"
											>
												<v-list-item-title>
													{{$t('print.print-receipt')}}
												</v-list-item-title>
											</v-list-item>
										</v-list>
									</v-menu>
								</v-col>
							</v-row>
						</v-col>
					</v-row>
				</v-card>
			</v-col>
		</v-row>
	</div>
</template>

<script>
import rules from '@/helpers/validation rules'
import { mapGetters, mapState } from 'vuex';
import { invoiceTypes, paymentMethod, type, userType, paymentMethodArray as paymentMethodArr, auditLogType, itemConfigType } from '@/helpers/enums';
import { printHtml, fixNumber } from '@/helpers/functions';
import ItemsNavigator from '@/components/refactored-components/ItemsNavigator.vue';
// import QuantityInput from '@/components/QuantityInput.vue';
import InvoicesTabs from '@/components/refactored-components/InvoicesTabs.vue'
import moment from 'moment';
import { useInvoicesCurrencies, setAllAsDefaultInsteadOfFavoritesOnSaleInvoice, hideTaxOnSaleInvoice } from '@/configs/global.config';
export default {
	name: 'SaleInvoice',
	props: {
		eventHub: { type: Object }
	},
	// components: { ItemsNavigator, QuantityInput },
	components: { InvoicesTabs, ItemsNavigator },
	data() {
		return {
			useInvoicesCurrencies,
			hideTaxOnSaleInvoice,
			// showBackBtn: false,
			alertDialog: false,
			confirmDiscardDialog: false,

			confirmPaymentMethodDialog: false,
			confirmPaymentMethodUtils: {
				needToPrint: false
			},

			dateAlert: {
				dialog: false,
				isDateChecked: false,
				needPrint: null,
				printType: null,
			},

			createInvoiceByTabsHandler: false,

			savedTableInputs: {},

			tableInputs: {
				quantity: 1
			},
			isEditItem: false,
			rowEditIndex: null,
			validationMessages: [],

			invoiceNo: 0,
			entries: [],
			activeItems: setAllAsDefaultInsteadOfFavoritesOnSaleInvoice ? 0 : 1,
			originalInvoiceEntires: [],

			searchItem: null,
			isSearchByBarcode: true,

			loading: false,
			submitLoading: false,

			paidFormatted: '',
			discountAmount: '',
			discountPercentage: '',
			formattedCurrencyPrice: '',

			isCurrentInvoicePosted: false,

			invoiceTypes,
			paymentMethod,
			type,
			userType,
			auditLogType,
			print: {
				receipt: 1,
				saleInvoiceReport: 2,
				deliveryReport: 3,
				receiveReport: 4,
			},
			rules,

			auditLog: [],

			// data
			submitData: {
				account: null,
				cashbox: null,
				date: null,
				paymentMethodId: paymentMethod.cash,

				paid: null,
				discount: null,
				vatPercent: null,
				notes: null,
				printingNotes: null,

				currencyId: this.$localStorage.currencyId,
				currencyPrice: 0,
			},
			deleteLoading: false,
			price: null,
			loadingRequestPrice: false,
			dialogPaymentMethod: false,
			reverseCurrencyPrice: false,
			selectedItem: {},
			selectedItems: [],
			date: null,
			formattedDate: null,
			menu: false,
			discountType: null,
			processDialog: false,
			invoiceDiscountType: true,
			previousRoute: null
		}
	},

	watch: {
		'$route.params.id'(val) {
			if (val) {
				// this.resetTableInputs();
				this.fetchInvoice().then(() => {
					if (this.createInvoiceByTabsHandler) {
						this.createInvoiceByTabsHandler({
							id: this.$route.params.id,
							name: this.invoiceNo
						});
						this.createInvoiceByTabsHandler = false;
					}
				});
			} else {
				this.goToLatestInvoice();
			}
		},

		'submitData.paymentMethodId'(val) {
			if (!this.isCurrentInvoicePosted) {
				if (val === paymentMethod.debt) {
					this.submitData.account = null
				} else if ([paymentMethod.cash, paymentMethod.creditCard].includes(val)) {
					this.submitData.account = this.configs.defaultCashCustomerId;
				}
			}
		},

		watchInputs() {
			if (!this.isEditItem && this.tableInputs.itemId) {
				this.savedTableInputs = this.tableInputs;
			}
		},
		
		isEditItem(val) {
			if (!val && !this.entries.find(c => c.itemId === this.savedTableInputs.itemId)) {
				this.$nextTick(() => {
					// this.$refs.table.resetInputs(this.savedTableInputs)
				})
			}
		},

		watchInvoicesAndCashBox() {
			if (!this.loading) {
				this.$nextTick(() => {
					this.eventHub.$emit('update-invoices', this.invoicesDrafts)
				})
			}
		}
	},

	computed: {
		...mapState({
			configs: state => state.configs.configs,
			isAdmin: state => state.auth.self.isAdmin,
			includeVatInPrice: state => state.configs.configs.isPriceIncludesVAT,
			cashboxes: state => state.cashboxes.cashboxes,
			saleableItemsTree: state => state.items.saleableItemsTree,
			accounts: state => state.accounts.accounts,
			invoices: state => state.invoices.invoices,
			items: state => state.items.items,
			permissionsForUser: state => state.auth.permissionsForUser,
			selfPermissions: state => state.auth.selfPermissions,
			currencies: state => state.currencies.currencies,
			itemsConfigs: state => state.items.itemsConfigs
		}),
		...mapGetters({
			getInvoiceById: 'invoices/getInvoiceById',
			getItemById: 'items/getItemById',
			getItemByBarcode: 'items/getItemByBarcode',
			getUnitById: 'units/getUnitById',
			getCurrencyById: 'currencies/getCurrencyById',
			getUserById: 'users/getUserById'
		}),

		// computed helpers
		contextMenuItems() {
			return [
				{ title: this.$t('edit'), icon: 'mdi-pencil', iconColor: 'edit-color', event: 'edit', class: 'edit-color--text' },
				{ title: this.$t('delete'), icon: 'mdi-delete', iconColor: 'alert-color', event: 'delete', class: 'alert-color--text' },
			]
		},

		headers() {
			const headers = [
				{
					text: this.$t('headers.serial'),
					name: 'no',
					value: 'no',
					setValueExpr: (val, index) => index + 1,
					noInput: true,
				},
				{
					text: this.$t('headers.barcode'),
					name: 'barcode',
					value: 'barcode',
					divider: true,
					noInput: true,
				},
				{
					text: this.$t('headers.item'),
					name: 'itemId',
					value: 'itemId',
					setValueExpr: val => this.items.find(c => c.id === val)?.name ?? val,
					inputRules: rules.required,
					width: 140,
					disableInput: this.isEditItem,
				},
				{
					text: this.$t('headers.quantity'),
					name: 'quantity',
					value: 'quantity',
					width: 110,
					inputRules: rules.number,
					writable: !this.isCurrentInvoicePosted,
					onWriteChange: (item, activeInput, index) => {						
						this.entries[index].amount = item.quantity * item.price;
						this.entries[index].discount = item.amount * (item.percentDiscount / 100)
						this.entries[index].totalNet =
							item.amount - this.entries[index].discount + (
								!this.includeVatInPrice
									? ((item.taxPercent || 0) / 100) * (item.amount - this.entries[index].discount)
									: 0
							);
					},
					setInputExpr: (inputs, activeInput) => {
						if (activeInput === 'amount') {
							return inputs.amount / inputs.price
						}
						return inputs.quantity;
					},
				},
				{
					text: this.$t('headers.price'),
					name: 'price',
					value: 'price',
					type: 'number'
				},
				{
					text: this.$t('headers.amount'),
					name: 'amount',
					value: 'amount',
					setInputExpr: (inputs, activeInput) => {
						if (activeInput === 'amount') {
							return inputs.amount;
						}
						return inputs.price * inputs.quantity;
					},
					type: 'number-format'
				},
				{
					text: this.$t('headers.percent-discount'),
					name: 'percentDiscount',
					value: 'percentDiscount',
					setInputExpr: (inputs, activeInput) => {
						if (activeInput !== 'percentDiscount') {
							let value = (inputs.discount * 100) / (inputs.amount || 1);
							const str = value.toString();
							if (str.includes('.') && str.split('.')[1].length > 2) {
								value = fixNumber(value);
							}
							return value || null
						} else {
							return inputs.percentDiscount
						}
					},
					type: 'number'
				},
				{
					text: this.$t('headers.discount'),
					name: 'discount',
					value: 'discount',
					setInputExpr: (inputs, activeInput) => {
						if (activeInput !== 'discount') {
							let value = (inputs.amount * (inputs.percentDiscount / 100));
							const str = value.toString();
							if (str.includes('.') && str.split('.')[1].length > 2) {
								value = fixNumber(value);
							}
							return value || null
						} else {
							return inputs.discount
						}
					},
					type: 'number-format'
				},
				!this.includeVatInPrice
					? {
						text: this.$t('headers.tax'),
						name: 'taxPercent',
						value: 'taxPercent',
						type: 'number'
					}
					: null,
				{
					text: this.$t('headers.total-net'),
					name: 'totalNet',
					value: 'totalNet',
					disableInput: true,
					setInputExpr: inputs => {
						return fixNumber(
							inputs.amount - inputs.discount + ((inputs.taxPercent || 0) / 100) *
							(inputs.amount - inputs.discount),
							2
						)
					},
					type: 'number-format'
				},
			].filter(Boolean);
			if (!this.isCurrentInvoicePosted) {
				headers.splice(
					headers.length, 0,
					{
						text: '',
						name: 'actions',
						value: 'actions',
						align: 'center',
						noInput: true,
						sortable: false
					}
				)
			}
			for (let i = 0; i < headers.length; i++) {
				headers[i].class =
					headers[i].class
						? headers[i].class + ' text-no-wrap text-caption font-weight-medium'
						: 'text-no-wrap text-caption font-weight-medium';
				headers[i].columnClass = headers[i].columnClass ? headers[i].columnClass + ' px-1' : 'px-1'
			}
			return headers
		},

		filteredAccounts() {
			return this.accounts.filter(
				c => c.type === type.sub && !c.isHidden &&
				(this.submitData.paymentMethodId === paymentMethod.debt ? c.id !== this.configs.defaultCashCustomerId : true)
			)
		},

		isAccountDefaultCashCustomer() {
			return this.configs.defaultCashCustomerId === this.submitData.account
		},

		watchInputs() {
			return `${this.tableInputs.itemId} | ${this.tableInputs.quantity} | ${this.tableInputs.price} | ${this.tableInputs.discount} | ${this.tableInputs.percentDiscount}`
		},

		watchInvoicesAndCashBox() {
			return `${this.invoices.length} | ${this.submitData.cashbox} | ${this.loading}`
		},

		isAllowedToUnPost() {
			return this.permissionsForUser.userType === userType.manager || this.isAdmin
		},

		invoicesDrafts() {
			return this.invoices
				.filter(c => !c.isPosted && c.cashboxId === this.submitData.cashbox)
				.map(c => ({ id: c.id, name: c.no }))
		},

		// computed invoice values
		entiresTotalNet() {
			return this.entries.map(c => c.totalNet).reduce((a, b) => a + b, 0)
		},

		vatAmount() {
			if (this.includeVatInPrice) {
				// OLD WAY TO GET VAT
				return fixNumber(this.totalNet - this.entriesTotalWithoutVat);
			} else {
				// New WAY
				return fixNumber(this.entries.reduce((res, a) => 
					res + (((a.taxPercent || 0) / 100) * (a.amount - a.discount)), 0
				))
			}
		},

		entriesTotalWithoutVat() {
			if (this.includeVatInPrice) {
				// OLD WAY TO GET PRICE WITHOUT VAT
				return fixNumber(
					(this.entiresTotalNet - this.submitData.discount) *
					(1 / (1 + (this.submitData.vatPercent / 100)))
				);
			} else {
				// New WAY
				return fixNumber(this.entiresTotalNet - this.submitData.discount - this.vatAmount);
			}
		},

		totalNet() {
			return fixNumber(this.entiresTotalNet - this.submitData.discount);
		},

		unpaidAmount() {
			return this.numberFormat(fixNumber((this.totalNet - this.submitData.paid), 3))
		},

		auditLogCreatedBy() {
			const auditLog = this.auditLog.find(c => c.type === auditLogType.create);
			if (auditLog && auditLog.userId) {
				return {
					username: this.getUserById(auditLog.userId)?.name,
					time: moment(auditLog.createdAtFormatted).format('hh:mm a')
				}
			}
			return null;
		},

		// helpers
		paymentMethodArray() {
			return paymentMethodArr().filter(c => {
				if (c.id === paymentMethod.cash) return true;
				else if (c.id === paymentMethod.creditCard) return !this.selfPermissions.invoiceCredit.isNone;
				else if (c.id === paymentMethod.internal) return !this.selfPermissions.invoiceInternal.isNone;
				else if (c.id === paymentMethod.debt) return !this.selfPermissions.invoiceDebt.isNone;
				else return false
			})
		},
		mainItems() {
			return this.activeItems === 1 
				? [
					{
						id: 0,
						name: this.$t('SaleInvoice.all-favorites'),
						children: this.filteredItems
					}
				]
				: this.saleableItemsTree 
		},
		subItems() {
			const items = this.items.filter(c =>
				(
					this.activeItems === 0 && !this.searchItem
						? c.parentId === this.selectedItem.id 
						: true
				) &&
				c.type === type.main
			).map(v => ({
				id: v.id,
				items: this.filteredItems.filter(c => 
					(c.parentId === v.id && c.parentId !== this.selectedItem.id) && 
					c.type === type.sub
				)
			}))
			if (this.activeItems === 0) {
				items.unshift({
					id: 0,
					items: this.filteredItems.filter(v => v.parentId === this.selectedItem.id && v.type === type.sub)
				})
			}
			return items;
		},
		filteredItems() {
			return this.items.filter(c => 
				(this.activeItems === 1 ? c.isFavourite : true) &&
				(
					this.searchItem
						? this.isSearchByBarcode
							? c.barcode !== null ? c.barcode.includes(this.searchItem) : null
							: c.name.toLowerCase().includes(this.searchItem.toLowerCase())
						: true
				) &&
				!c.isUnsellable
			)
		},
		itemIsSelected() {
			return (id) => Boolean(this.entries.find(v => v.itemId === id))
		},
		extraItems() {
			const itemId = this.itemsConfigs.find(v => v.configType === itemConfigType.extra)?.itemId;
			// const item = t
			return this.nodeFlatChildren(this.saleableItemsTree.find(v => v.id === itemId))?.filter(v => v.type === 2);
		},

		isReadonly() {
			return this.permissionsForUser.userType === userType.dataReader || 
				this.selfPermissions.saleInvoice.isReadonly
		},
	},

	methods: {
		formatDate(value) {
			this.menu = false;
			const d = new Date(value);

			// Change this to the format you want:
			this.formattedDate = d.toLocaleDateString('en-GB'); 
			// → DD/MM/YYYY
		},
		resetPrice() {
			if (this.submitData.paymentMethodId === 4) {
				for (let i = 0; i < this.entries.length; i++) {
					this.entries[i].price = 0
					this.entries[i].amount = 0
					this.entries[i].totalNet = 0
					this.entries[i].percentDiscount = 0
					this.entries[i].discount = 0
					if (this.entries[i].price !== 0) {
						this.entries[i].price = 0
						this.entries[i].amount = 0
						this.entries[i].totalNet = 0
						this.entries[i].percentDiscount = 0
						this.entries[i].discount = 0
					}
				}
				this.discountAmount = ''
				this.submitData.discount = 0
				this.paidFormatted = 0
				this.discountTotal = 0
				this.dialogPaymentMethod = false
				return this.entries
			}
		},
		fetchSuggestedPurchasePrice() {
			if (!this.tableInputs.itemId) {
				this.tableInputs.price = null;
				return
			};

			this.loadingRequestPrice = true
			this.$store.dispatch('invoices/fetchSuggestedPurchasePrice', { 
				itemId: this.tableInputs.itemId,
				accountId: this.submitData.account 
			}).then((data) => {
				if (this.submitData.paymentMethodId === this.paymentMethod.internal) {
					this.tableInputs.price = 0	
				} else {
					if (data.isCustom) {
						this.tableInputs.price = data.price;
					} else {
						this.tableInputs.price = this.getItemById(this.tableInputs.itemId)?.salePrice?.amount;
					}
				}
			}).finally(() => {
				this.loadingRequestPrice = false;
			})
		},

		fetchSuggestedPurchasePriceForAll() {
			this.loadingRequestPrice = true
			for (let i = 0; i < this.entries.length; i++) {
				this.$store.dispatch('invoices/fetchSuggestedPurchasePrice', { 
					itemId: this.entries[i].itemId,
					accountId: this.submitData.account 
				}).then((data) => {
					if (this.submitData.paymentMethodId === this.paymentMethod.internal) {
						this.tableInputs.price = 0	
					} else {
						if (data.isCustom) {
							this.entries[i].price = data.price;
							this.entries[i].amount = this.entries[i].quantity * this.entries[i].price;
							this.entries[i].totalNet = this.entries[i].amount - this.entries[i].discount;
						} else {
							this.entries[i].price = this.getItemById(this.entries[i].itemId)?.salePrice?.amount;
						}
					}
				}).finally(() => {
					this.loadingRequestPrice = false;
					this.alertDialog = false
				})
			}
		},

		// table functions
		addFavItem(item) {
			// if (!this.entries.length) {
			// this.loadingRequestPrice = true
			// this.$store.dispatch('invoices/fetchSuggestedPurchasePrice', { 
			// 	itemId: item.id,
			// 	accountId: this.submitData.account 
			// }).then((data) => {
				
			// }).finally(() => { this.loadingRequestPrice = false })			
			// }
			const itemInTable = this.entries.find(c => c.itemId === item.id);
			if (!itemInTable) {
				if (this.submitData.paymentMethodId === this.paymentMethod.internal) {
					this.tableInputs.price = 0
					const itemToAdd = {
						id: item.id,
						itemId: item.id,
						quantity: 1,
						price: 0,
						amount: 0,
						barcode: item.barcode,
						taxPercent: !this.includeVatInPrice ? 0 : null,
						percentDiscount: 0,
						discount: 0,
						totalNet: 0,
					}
					this.entries.push(itemToAdd)
				} else {
					this.loadingRequestPrice = true
					this.$store.dispatch('invoices/fetchSuggestedPurchasePrice', { 
						itemId: item.id,
						accountId: this.submitData.account 
					}).then((data) => {
						const amount = data.isCustom ? data.price * 1 : item.salePrice?.amount ?? 0;
						const itemToAdd = {
							id: item.id,
							itemId: item.id,
							quantity: 1,
							price: data.isCustom ? data.price : item.salePrice?.amount ?? 1,
							amount: amount,
							barcode: item.barcode,
							taxPercent: !this.includeVatInPrice ? item.taxPercent || this.submitData.vatPercent : null,
							percentDiscount: 0,
							discount: 0,
							totalNet: amount + (
								!this.includeVatInPrice
									? ((item.taxPercent || this.submitData.vatPercent || 0) / 100) * amount
									: 0
							),
						}
						this.entries.push(itemToAdd)
					}).finally(() => { this.loadingRequestPrice = false })			
				}
			} else {
				this.loadingRequestPrice = false;
				itemInTable.quantity = Number(itemInTable.quantity) + 1;
				itemInTable.amount = itemInTable.price * itemInTable.quantity;
				itemInTable.discount = itemInTable.amount * ((itemInTable.percentDiscount || 0) / 100);
				itemInTable.totalNet =
					itemInTable.amount - itemInTable.discount + (
						!this.includeVatInPrice
							? ((itemInTable.taxPercent || 0) / 100) * (itemInTable.amount - itemInTable.discount)
							: 0
					);
			}
		},

		addByBarcode() {
			const item = this.getItemByBarcode(this.searchItem);
			if (item) {
				this.addFavItem(item);	
				this.searchItem = null;
			}
		},

		resetTableInputs() {
			this.$refs.table.resetInputs({
				itemId: null,
				quantity: 1,
				price: null,
				amount: null,
				percentDiscount: null,
				discount: null,
				totalNet: null,
			})
		},

		addItem() {
			if (this.$refs.table.validate()) {
				const itemInTable = this.entries.find(c => c.itemId === this.tableInputs.itemId);
				if (!itemInTable) {
					this.entries.push({
						barcode: this.getItemById(this.tableInputs.itemId)?.barcode,
						...this.tableInputs
					});
				} else {
					itemInTable.quantity = Number(itemInTable.quantity) + Number(this.tableInputs.quantity);
					itemInTable.amount = itemInTable.quantity * itemInTable.price;
					itemInTable.discount = itemInTable.amount * ((itemInTable.percentDiscount || 0) / 100);
					itemInTable.totalNet =
						itemInTable.amount - itemInTable.discount +
						(!this.includeVatInPrice ? ((itemInTable.taxPercent || 0) / 100) * (itemInTable.amount - itemInTable.discount) : 0);
				}

				this.$nextTick(() => {
					// this.resetTableInputs();
					// this.$refs.table.resetInputsFocus()
				})
			}
			this.searchItem = null
		},

		editItem() {
			if (this.$refs.table.validate()) {
				this.entries[this.rowEditIndex].itemId = this.tableInputs.itemId
				this.entries[this.rowEditIndex].quantity = this.tableInputs.quantity
				this.entries[this.rowEditIndex].price = this.tableInputs.price;
				this.entries[this.rowEditIndex].taxPercent = !this.includeVatInPrice ? this.tableInputs.taxPercent : null;
				this.entries[this.rowEditIndex].amount = this.tableInputs.amount;
				this.entries[this.rowEditIndex].percentDiscount = this.tableInputs.percentDiscount;
				this.entries[this.rowEditIndex].discount = this.tableInputs.discount;
				this.entries[this.rowEditIndex].totalNet = this.tableInputs.totalNet;
				this.$refs.table.resetEdit();
				this.isEditItem = false;
				
				this.$nextTick(() => {
					this.resetTableInputs();
					this.$refs.table.resetInputsFocus()
				})
			}
		},

		deleteItem({ index }) {
			// this.resetEdit = true;
			// this.$refs.table.resetEdit();
			this.isEditItem = false;
			this.entries.splice(index, 1);
			if (!this.entries.length) {
				this.discountAmount = 0;
				this.discountPercentage = 0;
				this.submitData.discount = 0;
			}
			this.$nextTick(() => {
				// this.resetTableInputs();
				// this.$refs.table.resetInputsFocus()
			})
		},

		setRowIndex(rowData) {
			this.rowEditIndex = rowData.index;
			this.isEditItem = true;
			this.$nextTick(() => {
				// this.$refs.table.resetInputsFocus()
			})
		},

		deleteInvoice() {
			this.deleteLoading = true
			this.$store.dispatch('invoices/delete', { id: this.$route.params.id }).then(() => {
				this.$store.dispatch('invoices/fetchAll', { type: invoiceTypes.sales }).then((data) => {
					if (this.invoicesDrafts.length === 0) {
						this.createNewInvoice(true);
					} else {
						this.$router.replace({
							name: 'sale-invoice',
							params: { id: this.invoicesDrafts[this.invoicesDrafts.length - 1].id }
						})
					}
				}).then(() => {
					this.confirmDiscardDialog = false
				}).finally(() => {
					this.deleteLoading = false
				})
			})
		},

		printReport(printType) {
			this.submitLoading = true;
			let actionName;
			switch (printType) {
			case this.print.receipt: actionName = 'invoiceReceipt'; break;
			case this.print.saleInvoiceReport: actionName = 'saleInvoice'; break;
			case this.print.deliveryReport: actionName = 'delivery'; break;
			case this.print.receiveReport: actionName = 'receive'; break;
			}

			return this.$store.dispatch(`reports/${actionName}`, {
				storeId: this.$localStorage.storeId,
				invoiceId: this.$route.params.id,
			}).then(data => {
				if (printType === this.print.receipt) {
					printHtml(data);
				}
			}).finally(() => {
				this.submitLoading = false;
			})
		},

		updateEntries(isDiscard = false, isCheckOut) {
			const currentInvoice = this.getInvoiceById(this.$route.params.id);
			if (!currentInvoice) {
				return new Promise((resolve) => resolve());
			}
			const finalEntries = [];
			const areArraysEqual = (arr1, arr2, compareKeys) => {
				if (arr1.length !== arr2.length) {
					return false;
				}
				let isEqual = false;
				for (let i = 0; i < compareKeys.length; i++) {
					const key = compareKeys[i];
					isEqual = arr1.every((item, index) => item[key] === arr2[index][key])
					if (!isEqual) {
						return isEqual
					}
				}
				return isEqual
			}

			if (
				isDiscard ||
				this.isCurrentInvoicePosted ||
				areArraysEqual(this.entries, this.originalInvoiceEntires, ['itemId', 'totalNet'])
			) {
				return Promise.all([]);
			}

			this.entries.forEach((entry) => {
				const item = this.getItemById(entry.itemId);
				finalEntries.push({
					invoiceId: currentInvoice.id,
					itemId: item.id,
					quantity: {
						quantity: Number(entry.quantity),
						unitSizeId: item.unitId,
					},
					price: {
						amount: Number(entry.price),
						currencyId: this.$localStorage.currencyId
					},
					amount: {
						amount: Number(entry.amount),
						currencyId: this.$localStorage.currencyId
					},
					taxPercentage: !this.includeVatInPrice ? Number(entry.taxPercent) : null,
					taxAmount:
						!this.includeVatInPrice
							? {
								amount: (entry.taxPercent / 100) * (entry.amount - entry.discount),
								currencyId: this.$localStorage.currencyId
							}
							: null,
					totalAmount: {
						amount: entry.totalNet,
						currencyId: this.$localStorage.currencyId
					},
					notes: null,
					// FIXME is should to send discountPercentage to API?
					discountPercentage: Number(entry.percentDiscount),
					discountAmount: {
						amount: Number(entry.discount),
						currencyId: this.$localStorage.currencyId
					}
				})
			})
			
			if (!isCheckOut) {
				this.$eventBus.$emit('show-snackbar', this.$t('messages.saving-the-invoice-as-a-draft'));
			}

			return this.$store.dispatch('invoices/updateEntries', {
				invoiceType: invoiceTypes.sales,
				invoiceId: currentInvoice.id,
				entries: finalEntries
			}).then(() => {
				const message =
					isCheckOut
						? this.$t('messages.saving-entries-done-wait-checkout')
						: this.$t('messages.saving-entries-done')
				this.$eventBus.$emit('show-snackbar', false, message);
			})
		},

		checkout(needPrint, printType) {
			if (!this.dateAlert.isDateChecked && moment(this.submitData.date).isBefore(moment(), 'days')) {
				this.dateAlert.needPrint = needPrint;
				this.dateAlert.printType = printType;
				this.dateAlert.dialog = true;
				this.dateAlert.isDateChecked = true;
				return;
			} else if (this.dateAlert.isDateChecked) {
				this.dateAlert.needPrint = null;
				this.dateAlert.printType = null;
				this.dateAlert.dialog = false;
				this.dateAlert.isDateChecked = false;
			};

			this.submitLoading = true;

			if (printType !== this.print.receipt && this.isCurrentInvoicePosted) {
				this.printReport(printType)
			} else if (!this.isCurrentInvoicePosted) {
				const currentInvoice = this.getInvoiceById(this.$route.params.id);
				// this.updateEntries(false, true)
				// .then(() => {
				// this.$store.dispatch('invoices/updateInvoiceWithoutEntries', {
				// 	id: this.$route.params.id,
				// 	type: invoiceTypes.sales,
				// 	cashboxId: this.submitData.cashbox,
				// 	intermediaryAccountId: this.submitData.account,
				// 	date: this.submitData.date,
				// 	notes: this.submitData.notes,
				// 	printingNotes: this.submitData.printingNotes,
				// 	refDoc: null,
				// 	paymentWay: this.submitData.paymentMethodId,
				// 	paidAmount: {
				// 		amount: this.submitData.paid,
				// 		currencyId: this.$localStorage.currencyId
				// 	},
				// 	discountTotal: {
				// 		amount: this.submitData.discount,
				// 		currencyId: this.$localStorage.currencyId
				// 	},
				// 	totalNet: {
				// 		// FIXME change name of entriesTotalWithoutVat computed prop
				// 		// amount: this.totalNet,
				// 		amount: this.entriesTotalWithoutVat,
				// 		currencyId: this.$localStorage.currencyId
				// 	},
				// 	entries: []
				// })
				const finalEntries = []
				this.entries.forEach((entry) => {
					const item = this.getItemById(entry.itemId)
					finalEntries.push({
						invoiceId: currentInvoice.id,
						itemId: item.id,
						quantity: {
							quantity: Number(entry.quantity),
							unitSizeId: item.unitId,
						},
						price: {
							amount: Number(entry.price),
							currencyId: this.$localStorage.currencyId
						},
						amount: {
							amount: Number(entry.amount),
							currencyId: this.$localStorage.currencyId
						},
						taxPercentage: !this.includeVatInPrice ? Number(entry.taxPercent) : null,
						taxAmount: 
							!this.includeVatInPrice
								? {
									amount: (entry.taxPercent / 100) * (entry.amount - entry.discount),
									currencyId: this.$localStorage.currencyId
								}
								: null,
						totalAmount: {
							amount: entry.totalNet,
							currencyId: this.$localStorage.currencyId
						},
						notes: null,
						// FIXME is should to send discountPercentage to API?
						discountPercentage: Number(entry.percentDiscount),
						discountAmount: {
							amount: Number(entry.discount),
							currencyId: this.$localStorage.currencyId
						}
					})
				})
				this.$store.dispatch('invoices/update', {
					id: this.$route.params.id,
					type: invoiceTypes.sales,
					cashboxId: this.submitData.cashbox,
					intermediaryAccountId: this.submitData.account,
					date: this.submitData.date,
					notes: this.submitData.notes,
					refDoc: null,
					paymentWay: this.submitData.paymentMethodId,
					paidAmount: {
						amount: this.submitData.paid,
						currencyId: this.$localStorage.currencyId
					},
					discountTotal: {
						amount: this.submitData.discount,
						currencyId: this.$localStorage.currencyId
					},
					totalNet: {
						amount: this.entriesTotalWithoutVat,
						currencyId: this.$localStorage.currencyId
					},
					entries: finalEntries,
					alternativeCurrencyId:
						this.submitData.currencyId !== this.$localStorage.currencyId &&
						useInvoicesCurrencies
							? this.submitData.currencyId
							: null,
					currencyPrice:
						this.submitData.currencyId !== this.$localStorage.currencyId &&
						useInvoicesCurrencies
							? (this.submitData.currencyPrice ? 1 / this.submitData.currencyPrice : 0)
							: null
				}).then(() => {
					this.$store.dispatch('invoices/checkout', { id: currentInvoice.id })
						.then(() => {
							this.$eventBus.$emit('show-snackbar', false, this.$t('messages.checkout-done'))
							
							if (needPrint) {
								this.printReport(printType).finally(() => {
									this.submitLoading = false;
								})
							} else {
								this.submitLoading = false;
							}

							// this.resetTableInputs();
							this.isCurrentInvoicePosted = true;
							this.confirmPaymentMethodDialog = false;

							const latestInvoice = this.invoices[this.invoices.length - 1];
							if (latestInvoice.id === this.$route.params.id) {
								this.createNewInvoice(false);
							} else {
								this.$store.dispatch('invoices/fetchAll', { type: invoiceTypes.sales })
									.then(() => {
										this.eventHub.$emit('update-invoices', this.invoicesDrafts)
									});
								this.fetchInvoice();
							}
							this.processDialog = false;
						})
						.catch(() => {
							this.submitLoading = false;
						})
				}).catch(() => {
					this.submitLoading = false;
				})
			}
		},

		unPostInvoice() {
			this.submitLoading = true;
			this.$store.dispatch('invoices/unPost', {
				id: this.$route.params.id
			}).then(() => {
				this.$store.dispatch('invoices/fetchAll', { type: invoiceTypes.sales })
					.then(() => {
						this.eventHub.$emit('update-invoices', this.invoicesDrafts)
					});
				this.fetchInvoice();
			}).finally(() => {
				this.submitLoading = false;
			})
		},

		// helpers
		numberFormat(value) {
			return this.$options.filters.numberFormat(value);
		},

		getItemFullName(item) {
			return `${item.no} - ${item.name}`	
		},

		createNewInvoice(isFirstInvoice) {
			this.$store.dispatch('invoices/create', {
				type: invoiceTypes.sales, 
				cashboxId: this.submitData.cashbox,
				intermediaryAccountId: this.configs.defaultCashCustomerId,
				date: new Date().toISOString(),
				notes: null,
				refDoc: null,
				paymentWay: paymentMethod.cash
			}).then((id) => {
				// this.fetchInvoice()
				this.$store.dispatch('invoices/fetchAll', { type: invoiceTypes.sales });
				this.$router.replace({ name: 'sale-invoice', params: { id } });
				this.submitData.date = new Date().toISOString();
				
				if (isFirstInvoice) { this.invoiceNo = 1 };
			})
		},

		fetchInvoice() {
			this.loading = true;
			return this.$store.dispatch('invoices/fetchById', { id: this.$route.params.id })
				.then((data) => {
					this.isCurrentInvoicePosted = data.isPosted;

					this.invoiceNo = data.no;
					this.submitData.date = data.date;
					this.submitData.paymentMethodId = data.paymentWay;
					this.submitData.cashbox = data.cashboxId;
					this.submitData.account = data.intermediaryAccountId;
					this.submitData.paid = data.paidAmount?.amount;
					this.paidFormatted = this.numberFormat(data.paidAmount?.amount);
					this.submitData.discount = data.discountTotal?.amount;
					this.discountAmount = this.numberFormat(data.discountTotal?.amount);
					this.submitData.vatPercent = data.vatPercent;
					this.submitData.notes = data.notes;
					this.submitData.printingNotes = data.printingNotes;
					this.entries = [];
					data.entries.forEach((entry) => {
						this.entries.push({
							barcode: this.getItemById(entry.itemId)?.barcode,
							itemId: entry.itemId,
							quantity: entry.quantity.quantity ? entry.quantity?.quantity : 1,
							price: entry.price.amount,
							amount: entry.quantity.quantity * entry.price.amount,
							percentDiscount: entry.discountPercentage,
							discount: entry.discountAmount.amount,
							taxPercent: !this.includeVatInPrice ? entry.taxPercentage : null,
							totalNet: entry.totalAmount.amount,
						})
					})
					this.originalInvoiceEntires = this.entries.map(c => ({ ...c }));
					this.tableInputs.quantity = 1;
					this.submitData.currencyId = data.alternativeCurrencyId || this.$localStorage.currencyId;
					this.submitData.currencyPrice = data.currencyPrice || 0;
					this.formattedCurrencyPrice = this.numberFormat(1 / (data.currencyPrice || 1) || 0);
					this.auditLog = data.auditLogSimplifiedResponses || [];
					this.discountPercentage = this.fixNumber((Number(this.discountAmount) / (this.totalNet + Number(this.discountAmount))) * 100, 2) || 0;
				})
				.finally(() => {
					this.loading = false;
				})
		},

		goToLatestInvoice() {
			return this.$store.dispatch('invoices/fetchAll', { type: invoiceTypes.sales })
				.then((invoices) => {
					if (invoices.length === 0) {
						this.createNewInvoice(true);
					} else {
						// casher allowed to see his cashbox invoices
						const availableInvoices =
							invoices.filter(c =>
								this.permissionsForUser.userType === userType.casher
									? c.cashboxId === this.permissionsForUser.cashboxId
									: true
							);
						const latestInvoice = availableInvoices[availableInvoices.length - 1];

						if (latestInvoice.isPosted) {
							this.createNewInvoice(false);
						} else {
							this.$router.replace({ name: 'sale-invoice', params: { id: latestInvoice.id } });
							this.submitData.date = latestInvoice.date;
							this.invoiceNo = latestInvoice.no;
							this.loading = false;
						}
					}
				})
				.catch(() => {
					this.loading = false;
				})
		},

		// helpers
		moment,
		fixNumber,
		nodeFlatChildren(node) {
			if (!node?.children) return []
			return node?.children.reduce((arr, child) => {
				return arr.concat(this.items.find(v => v.id === child.id), this.nodeFlatChildren(child))
			}, [])
		},
	},

	created() {
		this.eventHub.$on('create-invoice', (handler) => {
			this.createInvoiceByTabsHandler = handler;
			this.createNewInvoice(false);
		})

		this.submitData.account = this.configs.defaultCashCustomerId;
		
		this.submitData.cashbox = this.permissionsForUser.userType === userType.casher ? this.$localStorage.cashboxId : this.configs.mainCashBoxId;

		// fetch last invoice / create new one
		this.loading = true;
		this.$store.dispatch('items/fetchAllAsTree', { sellableOnly: true });
		if (!this.$route.params.id) {
			this.goToLatestInvoice();
		} else {
			this.$store.dispatch('invoices/fetchAll', { type: invoiceTypes.sales });
			this.fetchInvoice();
		}
	},

	mounted() {
		this.tableInputs.quantity = '1';
		/*
			window.addEventListener('keydown', (e) => {
				console.log(e, e.code)
				if (e.code === 'Escape') {
					this.resetEdit = true;
				}
			})
		*/
	},
	beforeDestroy() {
		this.eventHub.$off('create-invoice');
		/*
			window.removeEventListener('keydown', (e) => {
				console.log(e, e.code)
				if (e.code === 'Escape') {
					this.resetEdit = true;
				}
			})
		*/
	},

	beforeRouteLeave(to, from, next) {
		this.updateEntries().then(() => { next(); })
	},

	beforeRouteUpdate(to, from, next) {
		this.updateEntries().then(() => { next(); })
	},

	beforeRouteEnter(to, from, next) {
		next(vm => {
			vm.previousRoute = ['all-invoices', 'journal-item', 'daily-journal'].includes(from.name) ? from : null;
			return true;
		})
	},

	metaInfo() {
		return {
			title: this.$t('routes.sale-invoice'),
			titleTemplate: '%s | POS'
		}
	}
}
</script>

<style lang="scss">
.table input {
	@media only screen and (min-width:960px) and (max-width:1264px) {
		font-size: 12px !important;
		font-weight: bold;
	}
}
#invoice { 
	.v-input {
		margin-top: 1px;
	}
	.bee-handy-smart-table__input-cell {
		padding: 0 8px;
	}
	#all-items-panels {
		.v-expansion-panel--active {
			> .v-expansion-panel-header  {
				min-height: 33px;
			}
		}
		.v-expansion-panel-header {
			min-height: 40px;
			padding: 0 16px;
		}
		.v-expansion-panel-content__wrap {
			padding: 0 12px;
		}
	}
	.v-slide-group__prev--disabled, .v-slide-group__next--disabled {
		display: none;
	}
	.v-slide-group__next, .v-slide-group__prev {
		min-width: 0%;
	}
	.calculated-number {
		line-height: 1.4rem;
	}
	.print-menu-btn {
		outline: 1px solid #eee;
	}
	.thin-border{
		border: 1px solid #dddddd !important;
	}
	.v-card{
		transition: 0.1s ease-in;
	}
	.grey-thin-border, .v-dialog .grey-thin-border {
		border: 1px solid grey !important;
	}
	.primary-thin-border{
		border: 1px solid #02855b;
	}
	.active-item-bg{
		background: #02855b15;
		border: 1px solid #02855b;
	}
	.active-item-color{
		color: #02855b98;
		
	}
	.hovered-item-bg{
		background: #02855b09;
	}
	// need to be replaced with a scss variables after apply the new ui to all the system
	#invoice-entry-expansion-panel {
		.v-expansion-panel-content__wrap {
			padding: 0;
		}
	}
	.minimized-height-input{
		input{
			min-height: 24px;
			padding: 0;
		}
	}
	.maximized-height-input{
		input{
			padding: 12px 0 !important;
			max-height: 40px !important;
		}
	}
	.minimized-input-padding{
		.v-input__slot{
			padding: 0 2px !important;
		}
	}
	.customized-rounded{
		border-radius: 8px !important;
	}
}
.w-100 {
	width: 100%
}
</style>